import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FaTimes, FaUserCircle, FaSpinner } from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

/**
 * Share a folder (project) OR a whole space.
 * Pass `folder` for project-scope, or `space` ({ space_path_id, space_path })
 * for space-scope. `onNavigateToSpace(spacePathId)` is called when the admin
 * chooses to manage a space-level grant surfaced from a folder context.
 */
export default function ShareModal({ folder, space, onClose, onNavigateToSpace }) {
  const token = sessionStorage.getItem("userToken");
  const isSpace = !!space;
  const displayName = isSpace
    ? space.space_path || space.name || "space"
    : folder?.folder_name;

  const [email, setEmail] = useState("");
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch current access grants (unified shape: {grants: [{...scope}]})
  const fetchGrants = async () => {
    const params = isSpace
      ? { space_path_id: space.space_path_id }
      : { folder_id: folder?.id };
    if ((isSpace && !space.space_path_id) || (!isSpace && !folder?.id)) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/ocr/folder-access/`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setGrants(res.data.grants || []);
    } catch (err) {
      console.error("Error fetching access grants", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder?.id, space?.space_path_id]);

  // Autocomplete search over org users
  useEffect(() => {
    if (email.trim().length < 1) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${BASE_URL}/ocr/org-users/`, {
          params: { q: email.trim() },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuggestions(res.data || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 250); // debounce
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Close suggestions on outside click
  useEffect(() => {
    function handleClick(e) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Grant access (folder → folder-access/; space → folder-access/bulk/)
  const handleShare = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSharing(true);
    setError("");
    setSuccess("");

    try {
      const url = isSpace
        ? `${BASE_URL}/ocr/folder-access/bulk/`
        : `${BASE_URL}/ocr/folder-access/`;
      const body = isSpace
        ? { space_path_id: space.space_path_id, user_email: email.trim() }
        : { folder_id: folder.id, user_email: email.trim() };
      const res = await axios.post(url, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(res.data.message);
      setEmail("");
      fetchGrants();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to share.");
    } finally {
      setSharing(false);
    }
  };

  // Revoke access. Folder grants delete directly; space grants surfaced from a
  // folder context are NOT silently deleted — we navigate the admin to the space.
  const handleRevoke = async (grant) => {
    setError("");
    setSuccess("");

    if (grant.scope === "space" && !isSpace) {
      // "via space share" — don't delete from folder context
      if (onNavigateToSpace) {
        onNavigateToSpace(grant);
      } else {
        setError(
          "This person has access via a whole-space share. Open the space to manage it."
        );
      }
      return;
    }

    try {
      if (grant.scope === "space") {
        await axios.delete(`${BASE_URL}/ocr/folder-access/bulk/`, {
          data: { space_path_id: space.space_path_id, user_email: grant.user_email },
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.delete(`${BASE_URL}/ocr/folder-access/`, {
          data: { folder_id: folder.id, user_email: grant.user_email },
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setSuccess("Access revoked.");
      fetchGrants();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to revoke.");
    }
  };

  const helperText = isSpace
    ? "People you add can view every project in this space, including ones created later."
    : "People you add can view this project and its results. They can't edit, delete, or run plays.";

  const initials = (nameOrEmail) =>
    (nameOrEmail || "?")
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0].toUpperCase())
      .join("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 truncate">
            Share "{displayName}"
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <FaTimes className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Add people form */}
          <form onSubmit={handleShare} className="relative">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Search by name or email..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={sharing || !email.trim()}
                className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sharing ? <FaSpinner className="animate-spin" /> : "Share"}
              </button>
            </div>

            {/* Helper text */}
            <p className="mt-2 text-xs text-gray-500">{helperText}</p>

            {/* Autocomplete dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute left-0 right-16 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto"
              >
                {suggestions.map((user) => (
                  <button
                    key={user.email}
                    type="button"
                    onClick={() => {
                      setEmail(user.email);
                      setShowSuggestions(false);
                    }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  >
                    <FaUserCircle className="w-6 h-6 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{user.fullName}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <span className="ml-auto text-xs text-gray-400">{user.role}</span>
                  </button>
                ))}
              </div>
            )}
          </form>

          {/* Feedback messages */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
          {success && (
            <p className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">{success}</p>
          )}

          {/* Divider */}
          <div className="border-t border-gray-200 pt-3">
            <p className="text-sm font-medium text-gray-700 mb-3">People with access</p>

            {/* Owner (folder scope only — we know the owner there) */}
            {!isSpace && folder?.email && (
              <div className="flex items-center gap-3 py-2">
                <FaUserCircle className="w-8 h-8 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{folder.email}</p>
                  <p className="text-xs text-gray-500">Owner</p>
                </div>
              </div>
            )}

            {/* Shared users */}
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <FaSpinner className="animate-spin text-gray-400" />
              </div>
            ) : (
              grants.map((grant) => {
                const viaSpace = grant.scope === "space" && !isSpace;
                return (
                  <div
                    key={`${grant.scope}-${grant.user_email}`}
                    className="flex items-center gap-3 py-2"
                  >
                    <span className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                      {initials(grant.user_name || grant.user_email)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {grant.user_name || grant.user_email}
                      </p>
                      <p
                        className="text-xs text-gray-500 truncate"
                        title={`Shared by ${grant.granted_by || "?"}${
                          grant.granted_at
                            ? " · " + new Date(grant.granted_at).toLocaleString()
                            : ""
                        }`}
                      >
                        {grant.user_email}
                      </p>
                    </div>
                    {/* Scope chip */}
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                        grant.scope === "space"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {grant.scope === "space" ? "Whole space" : "This project"}
                    </span>
                    {viaSpace && (
                      <span className="text-[10px] text-gray-400 flex-shrink-0">
                        via space share
                      </span>
                    )}
                    <button
                      onClick={() => handleRevoke(grant)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                      title={viaSpace ? "Manage in space" : "Revoke access"}
                    >
                      <FaTimes className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })
            )}

            {!loading && grants.length === 0 && (
              <p className="text-sm text-gray-400 py-2">
                Only you and admins can see this {isSpace ? "space" : "project"}. Add people to
                share it.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
